import { useEffect, useState } from 'react';
import { authActions } from '../../stores/authStore';

/**
 * 3-step TOTP enrolment wizard, opened from the "Authenticator App" card on
 * My Profile.
 *
 *   1. Server generates a secret + QR. User scans with their authenticator
 *      app (Google Authenticator, 1Password, Authy, Microsoft Authenticator).
 *   2. User types the first 6-digit code from the app. Server verifies and
 *      persists the encrypted secret.
 *   3. Server returns 10 single-use recovery codes (shown ONCE — the wizard
 *      forces a download / acknowledgement before the user can close).
 *
 * Cancellation at step 1 or 2 is safe — nothing has touched the DB yet (the
 * pending secret lives in a 10-min in-memory store on the server). At step 3
 * the user is enrolled; closing the modal without saving the codes is risky
 * but they can regenerate later.
 */

type Stage =
  | { kind: 'qr'; secret: string; qrDataUrl: string; otpauthUri: string }
  | { kind: 'verify'; secret: string; qrDataUrl: string; otpauthUri: string }
  | { kind: 'recovery'; recoveryCodes: string[] };

interface TotpSetupModalProps {
  /** Called when the user finishes (or cancels) enrolment. */
  onClose: (didEnrol: boolean) => void;
}

export function TotpSetupModal({ onClose }: TotpSetupModalProps) {
  const [stage, setStage] = useState<Stage | null>(null);
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  // Kick off enrolment as soon as the modal opens — the server mints a fresh
  // secret + QR on every /enroll/start call, so opening the modal is itself
  // the "begin" action.
  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    authActions.startTotpEnrollment()
      .then((r) => {
        if (cancelled) return;
        setStage({ kind: 'qr', ...r });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message || 'Failed to start enrolment');
      })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, []);

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (stage?.kind !== 'verify') return;
    setBusy(true);
    setError('');
    try {
      const r = await authActions.finishTotpEnrollment(code);
      setStage({ kind: 'recovery', recoveryCodes: r.recoveryCodes });
      setCode('');
    } catch (err) {
      setError((err as Error).message || 'Verification failed');
    } finally {
      setBusy(false);
    }
  }

  function downloadCodes() {
    if (stage?.kind !== 'recovery') return;
    const blob = new Blob(
      [
        `Asset Risk Manager — TOTP recovery codes\n`,
        `Generated: ${new Date().toISOString()}\n\n`,
        `Each code can be used ONCE if you lose access to your authenticator.\n`,
        `Store them somewhere safe (password manager, printed and locked away).\n\n`,
        ...stage.recoveryCodes.map((c) => `${c}\n`),
      ],
      { type: 'text/plain' }
    );
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `asset-risk-manager-recovery-codes-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    setAcknowledged(true);
  }

  function copyCodes() {
    if (stage?.kind !== 'recovery') return;
    void navigator.clipboard.writeText(stage.recoveryCodes.join('\n'));
    setAcknowledged(true);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[95vh] overflow-y-auto">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">
          {stage?.kind === 'recovery' ? 'Save your recovery codes' : 'Set up authenticator app'}
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          {stage?.kind === 'qr' && 'Step 1 of 3 — scan the QR with your authenticator app.'}
          {stage?.kind === 'verify' && 'Step 2 of 3 — confirm your authenticator works.'}
          {stage?.kind === 'recovery' && 'Step 3 of 3 — save these somewhere safe before closing.'}
          {!stage && 'Loading…'}
        </p>

        {error && (
          <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">{error}</div>
        )}

        {/* ─── Step 1: QR code ─── */}
        {stage?.kind === 'qr' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Open your authenticator app and use it to scan the QR code below. We recommend{' '}
              Google Authenticator, 1Password, Authy, or Microsoft Authenticator — any
              standards-compatible app works.
            </p>
            <div className="flex justify-center bg-gray-50 rounded-lg p-4">
              <img src={stage.qrDataUrl} alt="TOTP setup QR code" className="w-56 h-56" />
            </div>
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer hover:text-gray-700">Can't scan? Type the secret manually.</summary>
              <p className="mt-2">In your authenticator app, choose "Set up manually" and enter:</p>
              <div className="font-mono text-sm bg-gray-100 p-2 rounded mt-2 break-all select-all">
                {stage.secret}
              </div>
            </details>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setStage({ ...stage, kind: 'verify' })}
                className="px-4 py-2 text-sm text-white font-medium rounded-lg"
                style={{ background: 'var(--color-accent)' }}
              >
                I've scanned it
              </button>
            </div>
          </div>
        )}

        {/* ─── Step 2: verify ─── */}
        {stage?.kind === 'verify' && (
          <form onSubmit={handleVerify} className="space-y-4">
            <p className="text-sm text-gray-600">
              Type the 6-digit code your authenticator app is showing right now. The code changes
              every 30 seconds — make sure you use the current one.
            </p>
            <div>
              <label htmlFor="totp-code" className="block text-sm font-medium text-gray-700 mb-1">
                Verification code
              </label>
              <input
                id="totp-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none font-mono text-center tracking-widest text-lg"
                placeholder="123456"
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setStage({ ...stage, kind: 'qr' })}
                disabled={busy}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={busy || code.length !== 6}
                className="px-4 py-2 text-sm text-white font-medium rounded-lg disabled:opacity-50"
                style={{ background: 'var(--color-accent)' }}
              >
                {busy ? 'Verifying…' : 'Verify and enable'}
              </button>
            </div>
          </form>
        )}

        {/* ─── Step 3: recovery codes ─── */}
        {stage?.kind === 'recovery' && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
              These codes are shown <strong>once</strong>. Each is single-use and works as a
              fallback if you lose access to your authenticator app. Store them in a password
              manager or print them and lock them away.
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <ul className="grid grid-cols-2 gap-2 font-mono text-sm">
                {stage.recoveryCodes.map((c) => (
                  <li key={c} className="bg-white rounded px-3 py-1.5 border border-gray-200 text-center">
                    {c}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={downloadCodes}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Download as text file
              </button>
              <button
                type="button"
                onClick={copyCodes}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Copy to clipboard
              </button>
            </div>
            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                className="mt-1"
              />
              <span>
                I've saved these codes somewhere I can find them again. I understand each code
                works only once.
              </span>
            </label>
            <div className="flex justify-end">
              <button
                type="button"
                disabled={!acknowledged}
                onClick={() => onClose(true)}
                className="px-4 py-2 text-sm text-white font-medium rounded-lg disabled:opacity-50"
                style={{ background: 'var(--color-accent)' }}
              >
                Done
              </button>
            </div>
          </div>
        )}

        {!stage && (
          <div className="h-32 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}
